import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getSupabaseClient } from '../../lib/supabase';
import { PasswordInput } from '@ui';

type GuideInfo = {
  id: string;
  name: string;
  avatarUrl: string | null;
  tagline: string | null;
  about: string | null;
};

type GuideInviteResponse = {
  guide: GuideInfo;
};

type IdentifierStatus = 'idle' | 'loading' | 'not_found' | 'error' | 'ready';

export function ConnectInviteLanding() {
  const { identifier } = useParams<{ identifier: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<IdentifierStatus>('idle');
  const [guide, setGuide] = useState<GuideInfo | null>(null);
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const supabase = useMemo(() => {
    try {
      return getSupabaseClient();
    } catch (err) {
      console.error('Supabase client init failed', err);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!identifier || !supabase) {
      return;
    }

    let isActive = true;

    async function loadGuide() {
      setStatus('loading');
      try {
        const { data, error } = await supabase.functions.invoke<GuideInviteResponse>(
          'guide-invite-info',
          { body: { identifier } },
        );

        if (error) {
          console.error('Failed to load guide invite', error);
          if (!isActive) return;
          if (error.status === 404 || error.message === 'identifier_not_found') {
            setStatus('not_found');
          } else {
            setStatus('error');
          }
          return;
        }

        if (!data?.guide) {
          if (isActive) setStatus('not_found');
          return;
        }

        if (isActive) {
          setGuide(data.guide);
          setStatus('ready');
        }
      } catch (error) {
        console.error('Unexpected error loading guide invite', error);
        if (isActive) setStatus('error');
      }
    }

    void loadGuide();

    return () => {
      isActive = false;
    };
  }, [identifier, supabase]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!guide || !identifier || !supabase) {
      setSubmitError('Unable to create your account right now. Please try again later.');
      return;
    }

    if (!agreeTerms || !agreePrivacy) {
      setSubmitError('Please agree to the terms and privacy policy to continue.');
      return;
    }

    if (!formName.trim() || !formEmail.trim() || !formPassword) {
      setSubmitError('Please complete all required fields.');
      return;
    }

    if (formPassword.length < 8) {
      setSubmitError('Password must be at least 8 characters long.');
      return;
    }

    setLoading(true);

    try {
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: formEmail.trim().toLowerCase(),
        password: formPassword,
        options: {
          data: {
            full_name: formName.trim(),
          },
        },
      });

      if (signUpError) {
        throw signUpError;
      }

      const user = signUpData.user;

      if (!user) {
        throw new Error('Account creation pending email confirmation. Please check your inbox.');
      }

      const newSeekerId = crypto.randomUUID();
      const onboardingSessionId = crypto.randomUUID();

      // Use the edge function to create profile and seeker records (RLS-compliant)
      const { data: completeInviteData, error: completeInviteError } = await supabase.functions.invoke(
        'direct-connect-complete-invite',
        {
          body: {
            identifier,
            user_id: user.id,
            email: formEmail.trim().toLowerCase(),
            full_name: formName.trim(),
            seeker_id: newSeekerId,
            onboarding_session_id: onboardingSessionId,
          },
        }
      );

      if (completeInviteError) {
        console.error('direct-connect-complete-invite failed', completeInviteError);
        throw new Error('Unable to prepare your profile record. Please try again.');
      }

      if (!completeInviteData?.success) {
        throw new Error(completeInviteData?.error || 'Unable to prepare your profile record. Please try again.');
      }

      // Persist identifiers for onboarding and scheduling flows
      localStorage.setItem('ghost_seeker_id', newSeekerId);
      localStorage.setItem('direct_connect_guide_id', completeInviteData.guide_id || guide.id);
      localStorage.setItem('direct_connect_identifier', identifier);
      localStorage.setItem('direct_connect_active', 'true');
      localStorage.setItem('direct_connect_email', formEmail.trim().toLowerCase());
      localStorage.setItem('direct_connect_name', formName.trim());
      localStorage.setItem('direct_connect_user_id', user.id);
      localStorage.setItem('seeker_onboarding_session', onboardingSessionId);
      localStorage.removeItem('experience_preferences_data');
      localStorage.removeItem('medical_and_final_data');

      // Prefill onboarding storage with basic info we already have
      const basicInfoData = {
        seeker_id: newSeekerId,
        seeker_email: formEmail.trim().toLowerCase(),
        seeker_name: formName.trim(),
      };
      localStorage.setItem('basic_info_data', JSON.stringify(basicInfoData));

      navigate('/onboarding', { replace: true });
    } catch (error: any) {
      console.error('Failed to create account from invite', error);
      setSubmitError(error?.message || 'We could not create your account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#2C2731] text-[#FEF6D6]">
        <div className="text-center space-y-4">
          <div className="mx-auto h-12 w-12 rounded-full border-2 border-[#FEF6D6]/20 border-t-[#FEF6D6] animate-spin" />
          <p className="text-sm text-[#FEF6D6]/70">Preparing your invitation…</p>
        </div>
      </div>
    );
  }

  if (status === 'not_found') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#2C2731] px-4 text-[#FEF6D6]">
        <div className="w-full max-w-md rounded-2xl border border-[#FEF6D6]/20 bg-[#2C2731] p-8 text-center shadow-xl shadow-black/30">
          <h1 className="text-2xl font-semibold">Invalid invitation</h1>
          <p className="mt-3 text-sm text-[#FEF6D6]/80">
            We couldn't find this invite link. Please check with your guide to make sure you have the correct link.
          </p>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="mt-6 w-full rounded-full border border-[#FEF6D6]/40 px-4 py-2 text-sm font-semibold text-[#FEF6D6] transition hover:bg-[#FEF6D6]/10"
          >
            Go to home
          </button>
        </div>
      </div>
    );
  }

  if (status === 'error' || !guide) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#2C2731] px-4 text-[#FEF6D6]">
        <div className="w-full max-w-md rounded-2xl border border-[#FEF6D6]/20 bg-[#2C2731] p-8 text-center shadow-xl shadow-black/30">
          <h1 className="text-2xl font-semibold">Something went wrong</h1>
          <p className="mt-3 text-sm text-[#FEF6D6]/80">
            We're having trouble loading your invitation. Please refresh the page or try again later.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 w-full rounded-full border border-[#FEF6D6]/40 px-4 py-2 text-sm font-semibold text-[#FEF6D6] transition hover:bg-[#FEF6D6]/10"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const aboutPreview = guide.about?.split(/\n+/)[0]?.trim();

  return (
    <div className="flex min-h-screen flex-col bg-[#FFFCEF] text-[#2C2731]">
      <header className="flex items-center justify-between border-b border-[#2C2731]/10 bg-[#2C2731] px-4 py-3 text-[#FEF6D6]">
        <button
          onClick={() => navigate('/', { replace: true })}
          className="rounded-full px-3 py-1 text-sm font-medium hover:bg-[#FEF6D6]/10"
        >
          ← Back
        </button>
        <h1 className="text-lg font-semibold">Guides Collective</h1>
        <div className="w-16" />
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="grid w-full max-w-4xl gap-8 rounded-3xl border border-[#2C2731] bg-[#FFFCEF] p-8 shadow-[0_24px_50px_-32px_rgba(20,10,30,0.85)] md:grid-cols-2">
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              {guide.avatarUrl ? (
                <img
                  src={guide.avatarUrl}
                  alt={guide.name}
                  className="h-16 w-16 rounded-2xl object-cover"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#2C2731]/5 text-xl font-semibold text-[#2C2731]">
                  {guide.name.charAt(0)}
                </div>
              )}
              <div>
                <p className="text-sm uppercase tracking-wide text-[#2C2731]/70">Invitation from</p>
                <p className="text-2xl font-semibold text-[#2C2731] ph-mask">{guide.name}</p>
                {guide.tagline && <p className="text-sm text-[#2C2731]/70">{guide.tagline}</p>}
              </div>
            </div>
            <div className="rounded-2xl border border-[#2C2731]/15 bg-white/70 p-5 backdrop-blur">
              <h2 className="text-xl font-semibold text-[#2C2731]">Create your seeker profile</h2>
              <p className="mt-2 text-sm text-[#2C2731]/70 ph-mask">
                Accept this private invitation to connect directly with {guide.name.split(' ')[0] ?? 'your guide'}
                and schedule your first consultation in minutes.
              </p>
              {aboutPreview && (
                <p className="mt-4 text-sm italic text-[#2C2731]">"{aboutPreview}"</p>
              )}
            </div>
            <div className="rounded-2xl border border-[#2C2731]/15 bg-white/70 p-4 text-xs text-[#2C2731]/70">
              Your answers and sessions stay private between you and your guide. We only share what's needed to
              coordinate care.
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="mb-1 block text-sm font-medium text-[#2C2731]">Full name</label>
              <input
                type="text"
                value={formName}
                onChange={(event) => setFormName(event.target.value)}
                className="w-full rounded-2xl border border-[#2C2731]/30 bg-white px-4 py-3 text-sm text-[#2C2731] placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
                placeholder="Enter your full name"
                autoComplete="name"
                required
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-[#2C2731]">Email</label>
              <input
                type="email"
                value={formEmail}
                onChange={(event) => setFormEmail(event.target.value)}
                className="w-full rounded-2xl border border-[#2C2731]/30 bg-white px-4 py-3 text-sm text-[#2C2731] placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
                placeholder="Enter your email"
                autoComplete="email"
                required
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-[#2C2731]">Password</label>
              <PasswordInput
                value={formPassword}
                onChange={(event) => setFormPassword(event.target.value)}
                className="w-full rounded-2xl border border-[#2C2731]/30 bg-white px-4 py-3 text-sm text-[#2C2731] placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
                placeholder="Create a secure password"
                autoComplete="new-password"
                required
                minLength={8}
              />
              <p className="mt-1 text-xs text-[#2C2731]/60">Minimum 8 characters with letters and numbers</p>
            </div>

            <div className="space-y-3 rounded-2xl border border-[#2C2731]/15 bg-white/80 p-4 text-sm text-[#2C2731]/80">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-[#2C2731]/30 text-[#2C2731] focus:ring-[#2C2731]"
                  checked={agreeTerms}
                  onChange={(event) => setAgreeTerms(event.target.checked)}
                />
                <span>
                  I agree to the <span className="font-semibold text-[#2C2731]">Terms and Conditions</span>
                </span>
              </label>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-[#2C2731]/30 text-[#2C2731] focus:ring-[#2C2731]"
                  checked={agreePrivacy}
                  onChange={(event) => setAgreePrivacy(event.target.checked)}
                />
                <span>
                  I acknowledge the{' '}
                  <a href="/privacypolicy" className="font-semibold text-[#2C2731] underline" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                </span>
              </label>
            </div>

            {submitError && (
              <div className="rounded-2xl border border-[#B9383A]/20 bg-[#B9383A]/10 px-4 py-3 text-sm text-[#B9383A]">
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full rounded-2xl px-6 py-3 text-sm font-semibold text-[#FEF6D6] transition ${
                loading ? 'bg-[#2C2731]/50 cursor-not-allowed' : 'bg-[#2C2731] hover:bg-[#1F1924]'
              }`}
            >
              {loading ? 'Creating your account…' : 'Accept invitation'}
            </button>
            <p className="text-center text-xs text-[#2C2731]/60 ph-mask">
              By creating an account, you'll be connected directly with {guide.name.split(' ')[0] ?? 'your guide'}.
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
